/**
 * Archive, or restore, a class (O5, decision D10).
 *
 * Archiving keeps everything — roster, notes, curriculum, lessons, teaching
 * records — and cancels every meeting from today on, so the class leaves the
 * calendars and the reminders stop as a consequence. Restoring puts back the
 * meetings archiving cancelled that are still ahead; ones whose date passed
 * while archived stay cancelled, because they did not happen. Both writes go
 * through the term's revision, so a stale page is told to reload rather than
 * writing over a colleague's change.
 */

import { useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, SectionHeader } from "@/components/studio/primitives";

function archivedOn(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function ClassArchiveCard({ cls, onChanged }) {
  const [busy, setBusy] = useState(false);
  const archived = Boolean(cls.archived_at);

  const act = async (action) => {
    if (
      action === "archive" &&
      !window.confirm(
        "Archive this class?\n\nEvery meeting from today on is cancelled and the instructors are told. The roster, notes, curriculum and every lesson stay exactly as they are, and you can restore the class later.",
      )
    )
      return;
    setBusy(true);
    try {
      const res = await orgApi.post(`/org/classes/${cls.class_id}/${action}`, { expected_revision: cls.term_revision || 0 });
      if (action === "archive") {
        const n = res.data?.meetings_cancelled || 0;
        toast.success(n ? `Archived — ${n} upcoming meeting${n === 1 ? "" : "s"} cancelled` : "Archived");
      } else {
        const n = res.data?.meetings_restored || 0;
        toast.success(n ? `Restored — ${n} meeting${n === 1 ? "" : "s"} back on the calendar` : "Restored");
      }
      onChanged?.();
    } catch (error) {
      toast.error(getOrgErrorMessage(error, action === "archive" ? "Couldn't archive the class" : "Couldn't restore the class"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="class-archive-card">
      <SectionHeader>{archived ? "Archived" : "Archive"}</SectionHeader>
      {archived ? (
        <>
          <p className="mb-4 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]" data-testid="class-archived-since">
            Archived{archivedOn(cls.archived_at) ? ` on ${archivedOn(cls.archived_at)}` : ""}. Its meetings from that day on
            were cancelled; the roster, notes, curriculum and lessons are all still here. Restoring puts the
            remaining meetings back on the calendar. Meetings whose date has already passed stay cancelled.
          </p>
          <Button variant="ghost" onPress={() => act("unarchive")} disabled={busy} data-testid="restore-class">
            Restore class
          </Button>
        </>
      ) : (
        <>
          <p className="mb-4 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
            Archiving cancels every meeting from today on and takes the class off the calendars and out of
            reminders. Everything else stays: the roster, your notes on each student, the curriculum, every
            lesson and every teaching record. You can restore it at any time.
          </p>
          <Button variant="bare" onPress={() => act("archive")} disabled={busy} data-testid="archive-class">
            Archive class
          </Button>
        </>
      )}
    </Card>
  );
}
